import { useState } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'

interface Category {
  id: string
  name: string
  slug: string
  parent_id: string | null
  article_count: number
  children?: Category[]
}

interface Props {
  categories: Category[]
  selectedId: string
  onSelect: (id: string) => void
  onRefresh: () => void
}

export function KBCategoryBrowser({ categories, selectedId, onSelect, onRefresh }: Props) {
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const { toast, toastError } = useToast()

  const createCategory = async () => {
    if (!newName.trim()) return
    try {
      await api.post('/kb/categories', { name: newName.trim() })
      setNewName('')
      setAdding(false)
      toast('Category created')
      onRefresh()
    } catch (err) {
      toastError('Failed to create category', err)
    }
  }

  const updateCategory = async (id: string) => {
    if (!editName.trim()) return
    try {
      await api.patch(`/kb/categories/${id}`, { name: editName.trim() })
      setEditId(null)
      toast('Category updated')
      onRefresh()
    } catch (err) {
      toastError('Failed to update category', err)
    }
  }

  const deleteCategory = async (id: string) => {
    try {
      await api.delete(`/kb/categories/${id}`)
      if (selectedId === id) onSelect('')
      toast('Category deleted')
      onRefresh()
    } catch (err) {
      toastError('Failed to delete category', err)
    }
  }

  const renderCategory = (cat: Category, depth = 0) => (
    <div key={cat.id}>
      {editId === cat.id ? (
        <div style={{ display: 'flex', gap: 4, paddingLeft: depth * 16, marginBottom: 2, padding: '4px 8px' }}>
          <input
            type="text" className="input" value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') updateCategory(cat.id); if (e.key === 'Escape') setEditId(null) }}
            style={{ flex: 1, fontSize: 12, padding: '4px 6px' }}
            autoFocus
          />
          <button onClick={() => updateCategory(cat.id)} style={{ fontSize: 11, padding: '2px 6px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>&#10003;</button>
          <button onClick={() => setEditId(null)} style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer', color: 'var(--text-tertiary)' }}>&#10005;</button>
        </div>
      ) : (
        <div
          className={`kb-category-item ${selectedId === cat.id ? 'active' : ''}`}
          style={{ paddingLeft: 10 + depth * 16 }}
          onClick={() => onSelect(selectedId === cat.id ? '' : cat.id)}
          onContextMenu={e => {
            e.preventDefault()
            setEditId(cat.id)
            setEditName(cat.name)
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
          <span className="kb-category-count">{cat.article_count}</span>
          <button
            onClick={e => { e.stopPropagation(); deleteCategory(cat.id) }}
            style={{
              background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer',
              fontSize: 10, padding: '0 2px', opacity: 0, transition: 'opacity 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            title="Delete"
          >
            &#10005;
          </button>
        </div>
      )}
      {cat.children?.map(child => renderCategory(child, depth + 1))}
    </div>
  )

  return (
    <div className="kb-categories-panel">
      <div className="kb-categories-panel-header">
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Categories</span>
        <button
          onClick={() => setAdding(true)}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
          title="Add category"
        >
          +
        </button>
      </div>

      {/* All articles */}
      <div
        className={`kb-category-item ${!selectedId ? 'active' : ''}`}
        onClick={() => onSelect('')}
      >
        <span style={{ flex: 1 }}>All Articles</span>
      </div>

      {categories.map(cat => renderCategory(cat))}

      {adding && (
        <div style={{ display: 'flex', gap: 4, padding: '6px 8px', marginTop: 4 }}>
          <input
            type="text" className="input" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createCategory(); if (e.key === 'Escape') { setAdding(false); setNewName('') } }}
            placeholder="Category name"
            style={{ flex: 1, fontSize: 12, padding: '4px 6px' }}
            autoFocus
          />
          <button onClick={createCategory} style={{ fontSize: 11, padding: '2px 6px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>&#10003;</button>
          <button onClick={() => { setAdding(false); setNewName('') }} style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer', color: 'var(--text-tertiary)' }}>&#10005;</button>
        </div>
      )}

      {/* Add category link at bottom */}
      {!adding && (
        <button
          onClick={() => setAdding(true)}
          style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
            fontSize: 12, color: 'var(--text-disabled)', background: 'none', border: 'none',
            cursor: 'pointer', marginTop: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-disabled)'}
        >
          + New Category
        </button>
      )}
    </div>
  )
}
